/**
 * MUS-651: whether an account dialog is on screen: the paywall, or one drawn
 * the same way (billing, plan confirmation, pending deletion). These sit above
 * everything in a lesson. An announcement that does not block the app waits
 * while one is up: opened under it, its dialog would pull the focus to itself
 * and hide the visible paywall from screen readers, and nobody would see it.
 *
 * Kept free of React state and redux so any screen can report and read it,
 * the same shape as `Utils/paywallRequest`.
 */
import { useEffect, useSyncExternalStore } from 'react';

type Listener = () => void;

let dialogsShown: ReadonlySet<symbol> = new Set();
const listeners = new Set<Listener>();

const setDialogsShown = (next: ReadonlySet<symbol>) => {
  dialogsShown = next;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** True while any account dialog reports it is on screen. */
export const isAccountDialogShown = (): boolean => dialogsShown.size > 0;

/** Report, for as long as the calling component shows one, that an account dialog is up. */
export function useAccountDialogShown(shown: boolean): void {
  if (typeof shown !== 'boolean') {
    throw new TypeError('useAccountDialogShown: shown must be a boolean');
  }
  useEffect(() => {
    if (!shown) return undefined;
    const token = Symbol('account dialog');
    setDialogsShown(new Set([...dialogsShown, token]));
    return () => {
      setDialogsShown(new Set([...dialogsShown].filter((shownToken) => shownToken !== token)));
    };
  }, [shown]);
}

/** Whether an account dialog is on screen, re-read when that changes. */
export const useIsAccountDialogShown = (): boolean =>
  useSyncExternalStore(subscribe, isAccountDialogShown, isAccountDialogShown);
